import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  Container,
  MasterProductWithMenuContainerProduct,
  MenuContainerProduct,
  MenuContainerProductAttribute,
  MenuTag,
  PosMenu
} from '@/src/types/apps/gygMenuContainerProduct'
import { getSanitisedParams } from '../gygMenuContainer'
import { stringify } from 'qs'

export interface Modifier {
  id: number
  name: string
  glCode?: string | null
  description: string
  modifierId: number

  containerId: number
  parentProductId: number
  posPlu: number
  posMenuFlowId: number | null
  productType: 'P' | 'M'
  image: string | null
  imageTop: string | null
  imageAngle: string | null
  container: Container
  linearPrices: Record<string, string>
  menuContainerProductAttributes: MenuContainerProductAttribute[]
  menuTags: MenuTag[]
  posMenus: PosMenu[]
  sections: any[]
}

interface MenuContainerProductInitialState {
  menuContainerProducts?: MenuContainerProduct[]
  menuContainerProduct?: Modifier | null
  masterProductWithMenuContainerProduct?: MasterProductWithMenuContainerProduct | null
  loading: boolean
  success: string | null
  error: string | null
  isDialogOpen: boolean
  isMasterProductDialogOpen: boolean
  meta?: any
}

const initialState: MenuContainerProductInitialState = {
  masterProductWithMenuContainerProduct: null,
  loading: false,
  success: null,
  error: null,
  isDialogOpen: false,
  isMasterProductDialogOpen: false,
  menuContainerProducts: [],
  meta: null
}
interface MenuContainerProductResponse {
  meta: any
  data: any
}

interface uploadMenuContainersProductImagesResponse {
  data: any
}

export const saveMenuContainerProductTags = createAsyncThunk<
  MenuContainerProductTagsPayload,
  MenuContainerProductTagsPayload[],
  Redux
>('gygMenuContainerProducts/saveMenuContainerProductTag', async (menuTags, { extra }) => {
  const { data: responseData } = await extra.httpClient.put(`menu-container-product-tags/bulk`, menuTags)

  return responseData
})

export interface MenuContainerProductTagsPayload {
  menuContainerPosPlu: number
  menuContainerProductPosPlu: number
  posMenus: number[]
  tags: string[]
}

export const getMenuContainersProductsListByMasterProduct = createAsyncThunk<
  MenuContainerProductResponse,
  number,
  Redux
>('gygMenuContainerProducts/getMenuContainersProductsByMasterProduct', async (masterProductId, { extra }) => {
  const { data: responseData } = await extra.httpClient.get(`menu-container-products/master-product/${masterProductId}`)

  return responseData
})

export const getMenuContainersProductsWithoutMasterProduct = createAsyncThunk<MenuContainerProductResponse, any, Redux>(
  'gygMenuContainerProducts/getMenuContainersProductsWithoutMasterProduct',
  async (params, { extra }) => {
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const { data: responseData } = await extra.httpClient.get(
      `menu-container-products/without-master-product?${urlParams}`
    )

    return responseData
  }
)

export const getMenuContainerProductFull = createAsyncThunk<MenuContainerProductResponse, number, Redux>(
  'gygMenuContainerProducts/getMenuContainerProductFull',
  async (menuContainerProductId, { extra }) => {
    const { data: responseData } = await extra.httpClient.get(`menu-container-products/${menuContainerProductId}/full`)

    return responseData
  }
)

export const uploadMenuContainerProductImages = createAsyncThunk<
  uploadMenuContainersProductImagesResponse,
  FormData,
  Redux
>('gygMenuContainerProducts/uploadMenuContainerProductImages', async (payload, { extra }) => {
  const { data: responseData } = await extra.httpClient.post(`menu-container-products/images`, payload)

  return responseData
})

// Create the function for patching the product name and description
export const updateMenuContainerProduct = createAsyncThunk<
  any,
  { id: number; name: string; description: string },
  Redux
>('gygMenuContainerProducts/patchMenuContainerProduct', async (payload, { extra }) => {
  const { data: responseData } = await extra.httpClient.patch(`menu-container-products/${payload.id}`, {
    name: payload.name,
    description: payload.description
  })

  return responseData
})

// Search result type for pos-plu-or-name endpoint
export interface MenuContainerProductSearchResult {
  id: number
  name: string
  mcp_posPlu: number
}

// Search menu container products by posPlu or name
export const searchMenuContainerProductsByPosPluOrName = createAsyncThunk<
  MenuContainerProductSearchResult[],
  { posPlu: number; name: string },
  Redux
>('gygMenuContainerProducts/searchByPosPluOrName', async ({ posPlu, name }, { extra }) => {
  const params = new URLSearchParams()
  params.append('posPlu', posPlu.toString())
  params.append('name', name)

  const response = await extra.httpClient.get<{ data: MenuContainerProductSearchResult[] }>(
    `menu-container-products/pos-plu-or-name?${params.toString()}`
  )

  // Handle response structure: { data: [...] }
  if (response.data?.data && Array.isArray(response.data.data)) {
    return response.data.data
  }

  return []
})

// Get menu container products by posPlus array (POST — avoids WAF limits on long query strings)
export interface MenuContainerProductDetail {
  id: number
  name: string
  mcp_posPlu: number
}

// Search menu container products by posPlus array (POST — avoids WAF limits on long query strings)
export const getMenuContainerProductsByPosPlus = createAsyncThunk<MenuContainerProductDetail[], number[], Redux>(
  'gygMenuContainerProducts/getByPosPlus',
  async (posPlus, { extra }) => {
    if (!posPlus || posPlus.length === 0) {
      return []
    }

    const response = await extra.httpClient.post<MenuContainerProductDetail[] | { data: MenuContainerProductDetail[] }>(
      'menu-container-products/pos-plus/search',
      { posPlus }
    )

    // Handle different response structures
    if (Array.isArray(response.data)) {
      return response.data
    } else if (
      response.data &&
      typeof response.data === 'object' &&
      'data' in response.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data
    }

    return []
  }
)

const gygMenuContainerProductsSlice = createSlice({
  name: 'gygMenuContainerProducts',
  initialState,
  reducers: {
    setDialogOpen: (state, action) => {
      state.isDialogOpen = action.payload
    },
    setIsMasterProductDialogOpen: (state, action) => {
      state.isMasterProductDialogOpen = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMenuContainersProductsListByMasterProduct.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getMenuContainersProductsListByMasterProduct.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.masterProductWithMenuContainerProduct = action.payload.data
      })
      .addCase(getMenuContainersProductsListByMasterProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers product'
      })
      .addCase(getMenuContainersProductsWithoutMasterProduct.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getMenuContainersProductsWithoutMasterProduct.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainerProducts = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuContainersProductsWithoutMasterProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers product'
      })
      .addCase(getMenuContainerProductFull.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(getMenuContainerProductFull.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainerProduct = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuContainerProductFull.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers product'
      })
      .addCase(uploadMenuContainerProductImages.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(uploadMenuContainerProductImages.fulfilled, state => {
        state.loading = false
        state.success = 'uploaded'
      })
      .addCase(uploadMenuContainerProductImages.rejected, (state, action) => {
        state.loading = false
        state.success = null
        state.error = action.error.message ?? 'Failed to upload images'
      })
      .addCase(updateMenuContainerProduct.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(updateMenuContainerProduct.fulfilled, state => {
        state.loading = false
        state.success = 'updated'
      })
      .addCase(updateMenuContainerProduct.rejected, (state, action) => {
        state.loading = false
        state.success = null
        state.error = action.error.message ?? 'Failed to update product'
      })
      .addCase(searchMenuContainerProductsByPosPluOrName.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(searchMenuContainerProductsByPosPluOrName.fulfilled, state => {
        state.loading = false
        state.success = null

        // Results are handled in component, not stored in global state
      })
      .addCase(searchMenuContainerProductsByPosPluOrName.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to search products'
      })
  }
})

export const { setDialogOpen, setIsMasterProductDialogOpen } = gygMenuContainerProductsSlice.actions

export default gygMenuContainerProductsSlice.reducer
